import React, { useCallback, useEffect } from 'react';
import { Modal } from '../../Components/Modal/Modal';
import { UserForm } from '../../Components/UserForm/UserForm';
import { useAuth } from '../../contexts/AuthContext';
import { useUserActions } from '../../hooks/useUserActions';
import { UsersHeader } from './components/UsersHeader';
import { UsersContent } from './components/UsersContent';
import { ITEMS_PER_PAGE } from './constants';
import useUserModal from '../../hooks/useUserModal';
import useAgenciesList from '../../hooks/useAgenciesList';
import useUsersList from '../../hooks/useUsersList';
import { TableUser } from './types';
import { ConfirmationModal } from '../../Components/ConfirmationModal/ConfirmationModal';
import { useConfirmation } from '../../hooks/useConfirmation';

export const Users: React.FC<{ itemsPerPage?: number }> = ({ itemsPerPage = ITEMS_PER_PAGE }) => {
  const { userData } = useAuth();

  const { selectedUsers, setSelectedUsers, handleConfirmDelete } = useUserActions();

  const {
    isOpen: isConfirmationOpen,
    config: confirmationConfig,
    handleConfirm,
    openConfirmation,
    closeConfirmation,
    handleMultipleConfirmation,
    setSelectedItems,
  } = useConfirmation(handleConfirmDelete);

  useEffect(() => {
    setSelectedItems(selectedUsers);
  }, [selectedUsers, setSelectedItems]);

  const handleDeleteUser = useCallback(
    (userId: number) => {
      openConfirmation(
        {
          title: 'Are you sure you want to delete this item?',
          message:
            'This action cannot be undone. Once deleted, this item will be permanently removed.',
          variant: 'danger',
        },
        userId
      );
    },
    [openConfirmation]
  );

  const handleDeleteAll = useCallback(() => {
    handleMultipleConfirmation({
      title: 'Are you sure you want to delete these items?',
      message:
        'This action cannot be undone. Once deleted, these items will be permanently removed.',
      variant: 'danger',
    });
  }, [handleMultipleConfirmation]);

  const {
    isModalOpen,
    userToEdit,
    openModalForEdit,
    openModalForAdd,
    closeModal,
    handleSubmitForm,
  } = useUserModal();

  const {
    activeTab,
    filterBy,
    currentPage,
    usersData,
    onTabChange,
    onFilterChange,
    onPageChange,
    columns,
  } = useUsersList({ itemsPerPage, onEditUser: openModalForEdit, onDeleteUser: handleDeleteUser });

  const { agenciesList } = useAgenciesList();
  const agencyOptions = agenciesList.map((a) => ({ value: a.id.toString(), label: a.name }));

  const handleRowSelect = useCallback(
    (selectedRows: TableUser[]) => {
      setSelectedUsers(selectedRows.map((row) => row.id));
    },
    [setSelectedUsers]
  );

  return (
    <div className="users-page">
      <UsersHeader
        userData={userData}
        activeTab={activeTab}
        onTabChange={onTabChange}
        filterOptions={agencyOptions}
        filterBy={filterBy}
        onFilterChange={onFilterChange}
        selectedUsers={selectedUsers}
        onDeleteAll={handleDeleteAll}
        onAddUser={openModalForAdd}
      />

      <UsersContent
        activeTab={activeTab}
        columns={columns}
        data={usersData.users}
        onRowSelect={handleRowSelect}
        totalPages={usersData.totalPages}
        currentPage={currentPage}
        onPageChange={onPageChange}
        onDelete={handleDeleteUser}
      />

      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <UserForm onSubmit={handleSubmitForm} onCancel={closeModal} userId={userToEdit?.id} />
      </Modal>

      <Modal isOpen={isConfirmationOpen} onClose={closeConfirmation}>
        {confirmationConfig && (
          <ConfirmationModal
            {...confirmationConfig}
            onConfirm={handleConfirm}
            onCancel={closeConfirmation}
          />
        )}
      </Modal>
    </div>
  );
};
